import type { DrawAndPlaceAction } from "../types/action";
import type { Character } from "../types/character";
import type { HorrorRaceState } from "../types/game-state";
import type { Zone } from "../types/zone";
import { drawCoins } from "../utils/coin-pool";
import {
  appendDraw,
  getActingPlayerIndex,
  getCurrentDrawPass,
  getCurrentRaceDrawHistory,
} from "../utils/draw-history";
import { isFewPlayerVariant } from "../utils/player-count-rules";

export function applyDrawCoins(state: HorrorRaceState): HorrorRaceState {
  const { drawn, remaining } = drawCoins(state.coinPool, 3);
  // M1: record this turn's draw on the current race's history so the coin-
  // memory sheet can render it. The acting player is the one whose turn just
  // resolved into a draw (turnOrder[currentTurnIndex]); drawPass mirrors the
  // engine's pass counter.
  const nextHistory = appendDraw(getCurrentRaceDrawHistory(state), state, {
    playerIndex: getActingPlayerIndex(state),
    drawPass: getCurrentDrawPass(state),
    coins: drawn,
  });
  return {
    ...state,
    drawnCoins: drawn,
    coinPool: remaining,
    currentRaceDrawHistory: nextHistory,
  };
}

export function applyPlaceCoins(
  state: HorrorRaceState,
  action: DrawAndPlaceAction,
): HorrorRaceState {
  const newZones: Record<Zone, readonly Character[]> = {
    1: [...state.zones[1]],
    2: [...state.zones[2]],
    4: [...state.zones[4]],
  };

  for (const placement of action.placements) {
    newZones[placement.zone] = [
      ...newZones[placement.zone],
      placement.character,
    ];
  }

  const nextTurnIndex = state.currentTurnIndex + 1;
  const passComplete = nextTurnIndex >= state.turnOrder.length;
  const pass = state.drawPass ?? 1;

  // ≤5-player games draw twice per round: once pass 1 completes, start pass 2
  // (same round, no resolve yet).
  if (
    passComplete &&
    pass === 1 &&
    isFewPlayerVariant(state.config.playerCount)
  ) {
    return {
      ...state,
      zones: newZones,
      drawnCoins: [],
      currentTurnIndex: 0,
      drawPass: 2,
    };
  }

  return {
    ...state,
    zones: newZones,
    drawnCoins: [],
    currentTurnIndex: passComplete ? 0 : nextTurnIndex,
    drawPass: passComplete ? 1 : pass,
    phase: passComplete ? "ROUND_RESOLVE" : state.phase,
  };
}
